import { useState } from 'react';
import axios from '../../../api/apiClient';
import { domain, token } from '../../../components/Security';

/**
 * Generic useEntityPicker Hook
 * 
 * Reusable data fetching and filtering logic for any entity type
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.apiEndpoint - API endpoint to fetch data from
 * @param {Array} config.searchFields - Array of field configurations for filtering
 *   Example: [
 *     { label: "All", field: "all", index: 0 },
 *     { label: "Name", field: "name", index: 1 },
 *     { label: "Code", field: "code", index: 2 }
 *   ]
 */
export function useEntityPicker(config) {
  const [entities, setEntities] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const url = domain + config.apiEndpoint;
      const params = config?.params || {};

      const response = await axios.get(url, {
        params: params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setEntities(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error(`Error fetching ${config.entityName}:`, error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generic filter handler
   * @param {string} value - Search value
   * @param {number} filterIndex - Index from config.searchFields
   */
  const handleFilter = (value, filterIndex = 0) => {
    const lower = value.toLowerCase();

    // Find the matching filter config
    const filterConfig = config.searchFields.find(f => f.index === filterIndex);

    if (!filterConfig) {
      // Fallback: search all fields
      setEntities(filterData);
      return;
    }

    // If filter is "all", search across all searchable fields
    if (filterConfig.field === "all") {
      setEntities(
        filterData.filter((entity) => {
          return config.searchFields
            .filter(f => f.field !== "all")
            .some(f => {
              const fieldValue = entity[f.field];
              if (fieldValue === null || fieldValue === undefined) return false;
              return String(fieldValue).toLowerCase().includes(lower);
            });
        })
      );
    } else {
      // Search specific field
      setEntities(
        filterData.filter((entity) => {
          const fieldValue = entity[filterConfig.field];
          if (fieldValue === null || fieldValue === undefined) return false;
          return String(fieldValue).toLowerCase().includes(lower);
        })
      );
    }
  };

  const resetList = () => {
    setEntities(filterData);
  };

  return {
    entities,
    loading,
    fetchData,
    handleFilter,
    resetList
  };
}